import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/layout/SEO';
import { MaterialIcon } from '@/components/ui/MaterialIcon';

const privacySchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Protocols',
    description: 'startzone.dev privacy protocols, telemetry policy, and purge controls.',
});

const sections = [
    {
        id: '01',
        title: 'DATA_SHIELD ARCHITECTURE',
        body: (
            <>
                <p>
                    STARTZONE.DEV is delivered as a static experience through Cloudflare Pages, with application state
                    and secure backend flows routed through our connected services. We do not run ad-tech trackers or
                    sell behavioral profiles, and we keep the stored account surface intentionally narrow.
                </p>
                <div className="relative overflow-hidden bg-[#131313] px-5 py-5 md:px-6 md:py-6">
                    <div
                        className="pointer-events-none absolute inset-0 opacity-[0.16]"
                        style={{
                            background:
                                'linear-gradient(to bottom, transparent 50%, rgba(140, 172, 255, 0.08) 50%)',
                            backgroundSize: '100% 4px',
                        }}
                    />
                    <div className="relative mb-4 font-mono text-[9px] uppercase tracking-[0.28em] text-[#8cacff]">
                        [ CORE_PRINCIPIA ]
                    </div>
                    <ul className="relative space-y-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#d7dae3] sm:text-[11px]">
                        <li className="flex gap-3">
                            <span className="text-[#8cacff]">/</span>
                            NO_AD_TECH_TRACKERS
                        </li>
                        <li className="flex gap-3">
                            <span className="text-[#8cacff]">/</span>
                            MINIMIZED_ACCOUNT_SURFACE
                        </li>
                        <li className="flex gap-3">
                            <span className="text-[#8cacff]">/</span>
                            THIRD_PARTY_INFRA_DISCLOSED
                        </li>
                    </ul>
                </div>
            </>
        ),
    },
    {
        id: '02',
        title: 'TELEMETRY_INGESTION',
        body: (
            <>
                <p>
                    To maintain peak operational performance of the <span className="text-[#00cffc]">STARTZONE_OS</span>,
                    specific diagnostic vectors are monitored. We use privacy-focused Umami analytics to measure page
                    views and product flow at an aggregate level, alongside operational telemetry needed to keep the
                    interface responsive.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                    <div className="bg-[#1a1b1b] px-4 py-4 md:px-5 md:py-5">
                        <div className="mb-3 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#8cacff]">
                            <MaterialIcon icon="terminal" size={13} className="text-current" />
                            EDGE_DELIVERY
                        </div>
                        <p className="text-[12px] leading-6 text-[#8a8f99] md:text-[13px]">
                            Cloudflare Pages and related edge infrastructure necessarily process request metadata such as
                            IP address, user agent, and response timing to deliver and protect the site.
                        </p>
                    </div>
                    <div className="bg-[#1a1b1b] px-4 py-4 md:px-5 md:py-5">
                        <div className="mb-3 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#00cffc]">
                            <MaterialIcon icon="settings_input_component" size={13} className="text-current" />
                            UMAMI_EVENTS
                        </div>
                        <p className="text-[12px] leading-6 text-[#8a8f99] md:text-[13px]">
                            Umami is used for privacy-conscious analytics such as page views, referrers, and basic
                            device context so we can understand usage without running advertising profiles.
                        </p>
                    </div>
                </div>
            </>
        ),
    },
    {
        id: '03',
        title: 'ENCRYPTION_STANDARDS',
        body: (
            <>
                <p>
                    Traffic to the site is served over HTTPS via Cloudflare Pages, and backend requests to connected
                    services travel over TLS-protected channels. We rely on the security controls of those
                    infrastructure providers and avoid exposing raw application secrets in the client.
                </p>
                <div className="flex flex-wrap gap-2.5 pt-1">
                    {['HTTPS_DELIVERY', 'TLS_IN_TRANSIT', 'EDGE_CACHED_STATIC'].map((label) => (
                        <span
                            key={label}
                            className="bg-[#1a1b1b] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-[#70747c]"
                        >
                            {label}
                        </span>
                    ))}
                </div>
            </>
        ),
    },
    {
        id: '04',
        title: 'PURGE_MECHANISM',
        body: (
            <>
                <p>
                    If you want account data removed, use the support route to submit a deletion request. We will
                    review the request and remove or anonymize applicable account data within our control, subject to
                    operational, security, backup, and legal retention constraints.
                </p>
                <p>
                    Some records may persist for limited periods in provider-controlled logs, backups, or security
                    systems where immediate deletion is not technically possible or not legally permitted.
                </p>
                <Link
                    to="/contact"
                    className="group inline-flex flex-wrap items-center gap-3 bg-[#131313] p-1 pr-4 transition-[filter,background-color,transform] duration-200 hover:bg-[#171717] hover:brightness-110 active:scale-[0.985]"
                >
                    <span className="bg-[linear-gradient(135deg,#ff716c,#cf4c55)] px-3 py-2 font-['Space_Grotesk'] text-[11px] font-black uppercase tracking-[0.18em] text-[#2c0507] shadow-[0_0_18px_rgba(255,113,108,0.22)]">
                        INITIATE_WIPE
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#ff716c]/65 transition-colors group-hover:text-[#ff716c]">
                        ID: NULL_ALL_VECTORS
                    </span>
                </Link>
            </>
        ),
    },
] as const;

export default function PrivacyPage() {
    return (
        <div className="relative min-h-screen overflow-hidden bg-[#0e0e0e] text-white selection:bg-[#8cacff] selection:text-[#001f55]">
            <SEO
                title="Privacy Protocols"
                description="startzone.dev privacy protocols, telemetry policy, and purge controls."
                canonicalUrl="https://startzone.dev/privacy"
                schema={privacySchema}
            />

            <div className="pointer-events-none fixed inset-0 z-0">
                <div
                    className="absolute inset-0 opacity-[0.16]"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 1px 1px, #1c1d1d 1px, transparent 0)',
                        backgroundSize: '32px 32px',
                    }}
                />
                <div
                    className="absolute inset-0 opacity-[0.08]"
                    style={{
                        background: 'linear-gradient(to bottom, transparent 50%, rgba(0, 207, 252, 0.05) 50%)',
                        backgroundSize: '100% 4px',
                    }}
                />
                <div className="absolute left-1/2 top-[-8rem] h-[24rem] w-[44rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(0,207,252,0.08),transparent_72%)] blur-[120px]" />
                <div className="absolute right-[6%] top-[42%] h-[14rem] w-[14rem] rounded-full bg-[radial-gradient(circle,rgba(140,172,255,0.08),transparent_72%)] blur-[110px]" />
            </div>

            <div className="pointer-events-none absolute left-3 top-24 hidden opacity-[0.12] lg:block">
                <div className="flex flex-col gap-3">
                    <div className="h-1 w-1 bg-[#00cffc]" />
                    <div className="h-16 w-px bg-[#2b2d30]" />
                    <div className="h-1 w-1 bg-[#00cffc]" />
                </div>
            </div>

            <main className="relative z-10 mx-auto w-full max-w-[58rem] px-6 pb-28 pt-12 sm:px-8 md:pt-16 lg:px-10 lg:pb-32">
                <div className="mb-10 flex items-center gap-4 md:mb-14">
                    <Link
                        to="/"
                        className="group inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.26em] text-[#00cffc] transition-all hover:brightness-125"
                    >
                        <MaterialIcon icon="arrow_back" size={14} className="text-current transition-transform group-hover:-translate-x-0.5" />
                        [ RETURN_TO_BASE ]
                    </Link>
                    <div className="h-px flex-1 bg-[#202224]" />
                </div>

                <header className="mb-16 md:mb-20">
                    <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                        <div className="max-w-[38rem]">
                            <div className="mb-4 inline-flex items-center gap-2 bg-[#123641] px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#a9f4ff]">
                                SECURITY_LEVEL: OMEGA
                            </div>
                            <h1 className="font-['Space_Grotesk'] text-[3.1rem] font-black uppercase leading-[0.9] tracking-[-0.08em] text-white sm:text-[4.5rem] md:text-[5.2rem]">
                                PRIVACY
                                <br />
                                <span className="text-[#00cffc]">PROTOCOLS</span>
                            </h1>
                        </div>

                        <div className="space-y-1.5 pt-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#64686f] md:pb-2 md:text-right">
                            <div>
                                <span className="mr-2 text-[#8cacff]">+</span>
                                LAST_UPDATED: 2024.10.12
                            </div>
                            <div>
                                <span className="mr-2 text-[#d674ff]">+</span>
                                ENCRYPTED_STATUS: ACTIVE
                            </div>
                            <div>
                                <span className="mr-2 text-[#00cffc]">+</span>
                                VERSION: v1.0.5_s.4
                            </div>
                        </div>
                    </div>
                </header>

                <div className="space-y-14 md:space-y-16">
                    {sections.map((section) => (
                        <section key={section.id} className="group relative">
                            <div className="flex items-start gap-4 md:gap-6">
                                <div className="mt-1 hidden shrink-0 select-none font-mono text-[11px] uppercase tracking-[0.22em] text-[#2f3236] md:block">
                                    <div>{section.id}</div>
                                    <div>//</div>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="mb-5 flex items-center gap-4">
                                        <div className="font-['Space_Grotesk'] text-lg font-black uppercase tracking-[-0.03em] text-white md:text-[1.65rem]">
                                            {section.title}
                                        </div>
                                        <div className="h-px flex-1 bg-[#202224]" />
                                    </div>
                                    <div className="max-w-[42rem] space-y-6 text-[13px] leading-7 text-[#9499a1] md:text-[14px] md:leading-8">
                                        {section.body}
                                    </div>
                                </div>
                            </div>
                        </section>
                    ))}
                </div>

                <section className="mt-20 md:mt-24">
                    <div className="grid gap-4 md:grid-cols-[1.05fr_1fr_1fr]">
                        <div className="flex items-center gap-4 bg-[#131313] px-4 py-4 md:px-5">
                            <div className="flex h-9 w-9 items-center justify-center bg-[#00cffc] text-[#00323d]">
                                <MaterialIcon icon="shield" size={18} fill className="text-current" />
                            </div>
                            <div>
                                <div className="font-['Space_Grotesk'] text-[11px] font-black uppercase tracking-[0.18em] text-white">
                                    ST_PRIVACY_COUNCIL
                                </div>
                                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#64686f]">
                                    SECURED_BY_DISTRIBUTED_LOGIC
                                </div>
                            </div>
                        </div>

                        <div className="bg-[#131313] px-4 py-4 md:px-5">
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#64686f]">ST_PROTOCOLS</div>
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[#8cacff]">
                                ACTIVE // VERIFIED
                            </div>
                        </div>

                        <div className="bg-[#131313] px-4 py-4 md:px-5">
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#64686f]">DEBUG_LOGS</div>
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[#d674ff]">
                                SEALED // NO_PUBLIC_TRACE
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 flex flex-wrap items-center justify-between gap-5 border-t border-[#1d1f21] pt-6 font-mono text-[9px] uppercase tracking-[0.2em] text-[#50545a]">
                        <div className="flex flex-wrap items-center gap-5">
                            <Link to="/privacy" className="transition-colors hover:text-[#00cffc]">
                                ST_PROTOCOLS
                            </Link>
                            <Link to="/game-guide" className="transition-colors hover:text-[#00cffc]">
                                TERMINAL_API
                            </Link>
                            <Link to="/contact" className="transition-colors hover:text-[#00cffc]">
                                ENCRYPTED_SUPPORT
                            </Link>
                            <span>DEBUG_LOGS</span>
                        </div>
                        <div>[SYSTEM_READY] © 2026 STARTZONE_OS v1.0.5 // NO_PUBLIC_MIRROR_PRESENT</div>
                    </div>
                </section>
            </main>

            <div className="pointer-events-none fixed bottom-[16%] right-[8%] select-none opacity-[0.08]">
                <div className="font-['Space_Grotesk'] text-[6rem] font-black leading-none tracking-[-0.08em] text-[#00cffc] md:text-[7.5rem]">
                    SHIELD
                </div>
            </div>
        </div>
    );
}
